import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import {
  CheckCircle, Mail, Clock, Sparkles, ArrowRight, Star, Shield, Zap,
  MessageCircle, TrendingUp, Heart, Flame,
} from 'lucide-react';
import confetti from 'canvas-confetti';
import { OmMark, GoldDivider } from '../components/premium/Decor';

/**
 * Post-payment Thank You page.
 *
 * TWO jobs:
 *   1. Confirm the ₹99 Kundali payment (email delivery reassurance).
 *   2. AGGRESSIVELY upsell the ₹299 "30-min One-on-One Consultation with
 *      Astro Avinash". We deliberately do NOT specify the medium
 *      (chat / call / video) — per product policy the medium is
 *      revealed only after the customer commits.
 */
const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  const { language } = useLanguage();
  const navigate = useNavigate();
  const isHi = language === 'hi';

  // Urgency countdown (limited-time ₹299 offer)
  const [timeLeft, setTimeLeft] = useState({ m: 14, s: 59 });

  useEffect(() => {
    if (!orderId) return;
    // Meta pixel — Kundali Purchase (₹99) already fired earlier; keep this
    // as the source of truth so ad attribution windows work.
    let attempts = 0;
    const fire = () => {
      attempts += 1;
      if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
        try {
          window.fbq('track', 'Purchase', {
            value: 99, currency: 'INR',
            content_ids: [orderId],
            content_name: 'Karma Kundali Premium Report',
            content_type: 'product', num_items: 1,
          }, { eventID: `purchase_${orderId}` });
          window.fbq('track', 'CompleteRegistration',
            { value: 99, currency: 'INR', content_name: 'Kundali Order' },
            { eventID: `register_${orderId}` });
        } catch (e) { /* noop */ }
      } else if (attempts < 30) {
        setTimeout(fire, 200);
      }
    };
    fire();

    // Confetti
    const end = Date.now() + 2500;
    const frame = () => {
      confetti({ particleCount: 3, angle: 60, spread: 55, origin: { x: 0 }, colors: ['#73111E', '#D4AF37', '#F3E5AB'] });
      confetti({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1 }, colors: ['#73111E', '#D4AF37', '#F3E5AB'] });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();
  }, [orderId]);

  useEffect(() => {
    const t = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev.s > 0) return { ...prev, s: prev.s - 1 };
        if (prev.m > 0) return { m: prev.m - 1, s: 59 };
        return { m: 14, s: 59 };
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const fmt = (n) => String(n).padStart(2, '0');

  const bookConsultation = () => {
    if (window.fbq) {
      try {
        window.fbq('track', 'InitiateCheckout', {
          content_name: '30-min One-on-One Consultation',
          content_ids: ['kk-consultation-30'],
          value: 299, currency: 'INR', num_items: 1,
        });
      } catch (_) { /* noop */ }
    }
    navigate(`/consultation?ref=${orderId || ''}&src=success`);
  };

  const benefits = isHi ? [
    { Icon: Heart, t: 'शादी/रिश्तों में अटकाव — सटीक कारण + उपाय' },
    { Icon: TrendingUp, t: 'करियर + धन — अगले 2-3 साल की exact planning' },
    { Icon: Shield, t: 'दोष निवारण — Manglik, Kaal Sarpa, Rahu, Shani' },
    { Icon: Sparkles, t: 'Personal remedies — mantra, ratna, दान का सटीक tarika' },
    { Icon: Zap, t: 'बच्चों की पढ़ाई, health, family — जो भी पूछें' },
  ] : [
    { Icon: Heart, t: 'Marriage / relationship blockers — exact cause + fix' },
    { Icon: TrendingUp, t: 'Career + wealth — exact 2-3 year planning' },
    { Icon: Shield, t: 'Dosha remedies — Manglik, Kaal Sarpa, Rahu, Shani' },
    { Icon: Sparkles, t: 'Personal remedies — mantras, gems, right way to donate' },
    { Icon: Zap, t: "Children's studies, health, family — anything you ask" },
  ];

  return (
    <div data-testid="success-page" className="min-h-screen bg-brand-cream relative overflow-hidden">
      <div className="absolute inset-0 bg-mandala-watermark bg-repeat opacity-40 pointer-events-none" />
      <div className="absolute -top-32 -right-32 w-[520px] h-[520px] bg-mandala-large bg-no-repeat bg-center opacity-25 pointer-events-none" aria-hidden="true" />

      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        {/* Header */}
        <div className="text-center mb-5">
          <OmMark className="w-10 h-10 text-brand-maroon mx-auto mb-1.5" />
          <p className="text-brand-gold-dark uppercase tracking-[0.3em] text-xs font-semibold">
            Karma Kundali
          </p>
        </div>

        {/* Compact payment-success strip */}
        <div data-testid="payment-success-banner" className="bg-white rounded-2xl border-2 border-green-200 shadow-sm p-4 md:p-5 mb-6 flex items-center gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="w-7 h-7 text-green-700" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`font-playfair font-bold text-lg md:text-xl text-brand-maroon leading-snug ${isHi ? 'font-hindi-serif' : ''}`}>
              {isHi ? 'भुगतान सफल — आपकी कुंडली तैयार हो रही है' : 'Payment successful — your Kundali is being prepared'}
            </p>
            <p className={`text-brand-text-soft text-xs md:text-sm mt-0.5 ${isHi ? 'font-hindi' : 'font-outfit'}`}>
              {isHi
                ? 'आपकी 55+ पेज की रिपोर्ट 4 घंटे के अंदर आपकी email पर आ जाएगी।'
                : 'Your 55+ page report will arrive on your email within 4 hours.'}
            </p>
            {orderId && (
              <p data-testid="success-order-id" className="text-[10px] md:text-xs text-brand-text-mute mt-1 font-mono truncate">
                Order: {orderId}
              </p>
            )}
          </div>
        </div>

        {/* ─── AGGRESSIVE ₹299 CONSULTATION UPSELL ─── */}
        <div
          data-testid="consultation-upsell-card"
          className="relative bg-gradient-to-br from-brand-maroon via-brand-maroon-dark to-[#3a050d] rounded-3xl border-2 border-brand-gold shadow-premium overflow-hidden"
        >
          <div className="absolute inset-0 bg-mandala-large bg-center bg-no-repeat opacity-15 pointer-events-none" />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(circle at 50% 0%, rgba(243,229,171,0.28) 0%, transparent 55%)' }}
          />

          {/* Urgency ribbon */}
          <div className="relative bg-gradient-to-r from-brand-gold-dark via-brand-gold to-brand-gold-dark text-brand-maroon-dark text-center py-2.5 px-4 border-b border-brand-gold-light/60">
            <div className="inline-flex items-center gap-2 text-xs md:text-sm font-black uppercase tracking-widest">
              <Flame className="w-4 h-4 animate-pulse-soft" />
              <span className={isHi ? 'font-hindi' : 'font-outfit'}>
                {isHi ? 'सिर्फ़ आज के लिए · Offer expire होगा' : 'Today only · Offer expiring in'}
              </span>
              <span className="font-mono bg-brand-maroon-dark text-brand-gold-light rounded px-2 py-0.5">
                {fmt(timeLeft.m)}:{fmt(timeLeft.s)}
              </span>
            </div>
          </div>

          <div className="relative p-6 md:p-10">
            {/* Eyebrow */}
            <div className="text-center mb-3">
              <span className="inline-block bg-brand-gold text-brand-maroon-dark text-[10px] md:text-xs font-black uppercase tracking-[0.24em] px-3 py-1 rounded-full">
                {isHi ? 'विशेष अवसर · एक बार का ऑफर' : 'Special One-Time Offer'}
              </span>
            </div>

            {/* Headline */}
            <h2 className={`text-center font-playfair font-black text-brand-gold-light text-2xl md:text-4xl leading-tight ${isHi ? 'font-hindi-serif' : ''}`}>
              {isHi ? (
                <>Astro Avinash से <span className="text-white">30-मिनट</span> का<br className="hidden sm:block"/> personal One-on-One Consultation</>
              ) : (
                <>30-Minute Personal One-on-One<br className="hidden sm:block"/> Consultation with Astro Avinash</>
              )}
            </h2>
            <GoldDivider className="!my-4" />
            <p className={`text-center text-white/85 text-sm md:text-base leading-relaxed max-w-xl mx-auto ${isHi ? 'font-hindi' : 'font-outfit'}`}>
              {isHi
                ? 'PDF रिपोर्ट पढ़ना एक बात है — Astro Avinash से सीधे 30 मिनट अपनी कुंडली पर बात करना बिलकुल दूसरी बात है। आपके सारे उलझे हुए सवालों के जवाब, सटीक remedies, पूरी clarity।'
                : "Reading a PDF is one thing — speaking directly with Astro Avinash for 30 minutes on YOUR chart is a completely different thing. All your tangled questions answered, exact remedies, full clarity."}
            </p>

            {/* Pricing */}
            <div className="mt-6 flex flex-col items-center gap-1">
              <div className="flex items-baseline gap-3">
                <span className="text-white/45 line-through font-outfit text-2xl md:text-3xl">₹1499</span>
                <span data-testid="upsell-price" className="font-playfair font-black text-6xl md:text-7xl text-brand-gold-light">
                  ₹299
                </span>
              </div>
              <span className="bg-brand-gold text-brand-maroon-dark text-[10px] md:text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full">
                {isHi ? '80% छूट — सिर्फ अभी' : '80% OFF — right now'}
              </span>
            </div>

            {/* CTA */}
            <button
              type="button"
              data-testid="book-consultation-btn"
              onClick={bookConsultation}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-brand-gold hover:bg-brand-gold-light text-brand-maroon-dark font-black text-base md:text-lg px-6 py-4 rounded-full shadow-gold-glow hover:-translate-y-0.5 hover:shadow-premium transition-all uppercase tracking-wider"
            >
              <MessageCircle className="w-5 h-5" />
              <span className={isHi ? 'font-hindi' : 'font-outfit'}>
                {isHi ? 'हाँ, मुझे Astro Avinash से मिलना है' : 'Yes, I Want To Talk To Astro Avinash'}
              </span>
              <ArrowRight className="w-5 h-5" />
            </button>
            <p className={`text-center text-white/65 text-xs mt-2 ${isHi ? 'font-hindi' : 'font-outfit'}`}>
              {isHi
                ? 'Secure Razorpay · Book करने के 4-6 घंटे में slot fix हो जाएगा'
                : 'Secure Razorpay · Slot confirmed within 4-6 hours of booking'}
            </p>

            {/* Benefits list */}
            <div className="mt-7 bg-white/8 backdrop-blur-sm rounded-2xl p-5 border border-brand-gold/25">
              <p className={`text-brand-gold-light text-xs uppercase tracking-[0.24em] font-black mb-3 ${isHi ? 'font-hindi' : 'font-outfit'}`}>
                {isHi ? 'इन 30 मिनट में आपको क्या मिलेगा' : 'What you get in these 30 minutes'}
              </p>
              <ul className="space-y-2.5">
                {benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-gold/25 flex items-center justify-center mt-0.5">
                      <b.Icon className="w-3.5 h-3.5 text-brand-gold-light" />
                    </div>
                    <span className={`text-sm text-white/90 leading-relaxed ${isHi ? 'font-hindi' : 'font-outfit'}`}>
                      {b.t}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Social proof */}
            <div className="mt-5 grid grid-cols-3 gap-2 text-center">
              <div className="bg-white/8 rounded-xl p-3">
                <div className="flex items-center justify-center gap-0.5 text-brand-gold-light mb-0.5">
                  {[1,2,3,4,5].map(i => <Star key={i} className="w-3 h-3 fill-current" />)}
                </div>
                <p className="text-[10px] md:text-xs text-white/75">4.9 · 3,200+</p>
              </div>
              <div className="bg-white/8 rounded-xl p-3">
                <Sparkles className="w-4 h-4 text-brand-gold-light mx-auto mb-0.5" />
                <p className="text-[10px] md:text-xs text-white/75">
                  {isHi ? '15+ साल अनुभव' : '15+ yrs experience'}
                </p>
              </div>
              <div className="bg-white/8 rounded-xl p-3">
                <Shield className="w-4 h-4 text-brand-gold-light mx-auto mb-0.5" />
                <p className="text-[10px] md:text-xs text-white/75">
                  {isHi ? '100% Confidential' : '100% Private'}
                </p>
              </div>
            </div>

            {/* Small "skip" link — psychologically anchors the primary CTA */}
            <p className="mt-6 text-center">
              <button
                type="button"
                onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className={`text-white/50 hover:text-white/80 underline underline-offset-4 text-xs ${isHi ? 'font-hindi' : 'font-outfit'}`}
                data-testid="skip-consultation-link"
              >
                {isHi ? 'नहीं धन्यवाद, अभी मेरी कुंडली का इंतज़ार करता हूँ' : 'No thanks, I will just wait for my Kundali'}
              </button>
            </p>
          </div>
        </div>

        {/* Kundali delivery timeline (below the fold) */}
        <div className="mt-8 bg-white rounded-2xl border border-brand-gold/30 p-5 md:p-6">
          <div className="flex items-center gap-3 mb-3">
            <Mail className="w-5 h-5 text-brand-maroon" />
            <h3 className={`font-playfair font-bold text-lg text-brand-maroon ${isHi ? 'font-hindi-serif' : ''}`}>
              {isHi ? 'आपकी कुंडली का status' : 'Your Kundali delivery'}
            </h3>
          </div>
          <div className="flex items-center gap-2 text-sm text-brand-text-soft">
            <Clock className="w-4 h-4 text-brand-gold-dark" />
            <span className={isHi ? 'font-hindi' : 'font-outfit'}>
              {isHi
                ? '4 घंटे के भीतर आपकी email पर — कोई भी सवाल हो तो '
                : 'Within 4 hours on your email — for any question '}
              <a href="mailto:care@karmakundali.org" className="text-brand-maroon underline">care@karmakundali.org</a>
            </span>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-brand-text-mute text-xs mt-6">
          Karma Kundali · {isHi ? 'एस्ट्रो अविनाश द्वारा' : 'By Astro Avinash'}
        </p>
      </div>
    </div>
  );
};

export default SuccessPage;
